/**
 * Candidats d'identite par champ, pour que la secretaire CHOISISSE
 * ce qu'elle garde (mail Nicolas 13/09 : Data Matrix, CNI, ordo…).
 *
 * On n'invente pas de valeur : on liste celles deja presentes sur le
 * rendez-vous, la lecture INS, l'OCR d'ordonnance et la mutuelle.
 */

function normaliser(valeur) {
  return String(valeur || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[-'’]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function pousser(liste, valeur, source) {
  const v = String(valeur || "").trim();
  if (!v) return;
  const cle = normaliser(v);
  if (liste.some((c) => normaliser(c.valeur) === cle && c.source === source)) return;
  const meme = liste.find((c) => normaliser(c.valeur) === cle);
  if (meme) {
    if (!meme.sources.includes(source)) meme.sources.push(source);
    return;
  }
  liste.push({ valeur: v, source, sources: [source] });
}

/**
 * Les prenoms d'un code lu, quelle que soit la forme sous laquelle ils arrivent.
 *
 * Retour de Nicolas du 21/09, 14h10 : « Ce dossier n'a pas pu s'afficher »,
 * avec `(t.firstNames || []).join is not a function`. Le garde `|| []` ne
 * protege que l'ABSENCE ; il ne protege pas d'une valeur presente qui n'est
 * pas un tableau. Une chaine est vraie, elle traverse le `||`, et `.join`
 * n'existe pas dessus.
 *
 * Les lectures n'ont jamais promis un tableau : le Datamatrix INS rend une
 * liste, d'autres lectures rendent une chaine deja assemblee. C'est au
 * back-office d'accepter les deux, et non aux lectures de se ressembler.
 *
 * Le meme defaut existait en silence sur le prenom d'usage : `[0]` sur une
 * chaine rend sa PREMIERE LETTRE. « Sarah » devenait « S », sans erreur et
 * sans que personne ne le voie.
 */
export function prenomsLus(valeur) {
  if (Array.isArray(valeur)) return valeur.filter(Boolean).join(" ");
  return String(valeur ?? "").trim();
}

/** Le premier prenom d'un code lu, meme quand ils arrivent en une seule chaine. */
export function premierPrenomLu(valeur) {
  if (Array.isArray(valeur)) return valeur.filter(Boolean)[0] || "";
  return String(valeur ?? "").trim().split(/\s+/)[0] || "";
}

const CHAMPS = [
  { id: "lastNameBirth", label: "Nom de naissance", appointment: "lastName", ins: (t) => t.lastName, ia: (p) => p.nomNaissance || p.nom, champRdv: "lastName" },
  { id: "firstNamesBirth", label: "Prénoms de naissance", appointment: "firstNamesBirth", ins: (t) => prenomsLus(t.firstNames) || t.firstName, ia: (p) => p.prenomsSupplementaires || p.prenom, champRdv: "firstNamesBirth" },
  { id: "firstNamesUsed", label: "Prénom (usage)", appointment: "name", ins: (t) => premierPrenomLu(t.firstNames) || t.firstName, ia: (p) => p.prenom, champRdv: "name" },
  { id: "dob", label: "Date de naissance", appointment: "birthday", ins: (t) => t.birthday, ia: (p) => p.dateNaissance, champRdv: "birthday" },
  { id: "sex", label: "Sexe", appointment: "sex", ins: (t) => t.sex, ia: (p) => p.sexe, champRdv: "sex" },
  { id: "nir", label: "NIR", appointment: "vital", ins: (t) => t.insNumber, ia: (p) => p.numeroSecuriteSociale, champRdv: "vital" },
];

/*
  Nicolas, 01/10 : la mutuelle ne sert pas a l'identite « pour l'instant ».
  Elle reste affichee dans sa carte, mais n'est plus proposee comme source du
  nom, du prenom ou de la date de naissance. Remettre a true pour la rebrancher.
*/
export const MUTUELLE_SOURCE_IDENTITE = false;

export function candidatsIdentite(appointment = {}, { avecMutuelle = MUTUELLE_SOURCE_IDENTITE } = {}) {
  const traits = appointment.insReading?.traits || {};
  const ia = appointment.analysisResult?.patientInfo || {};
  const mutuelle = (avecMutuelle && appointment.mutuelleResume) || {};
  const retenus = appointment.identityRetenue || {};

  return CHAMPS.map((champ) => {
    const options = [];
    pousser(options, appointment[champ.appointment], "Dossier / saisie");
    pousser(options, champ.ins(traits), "Data Matrix INS");
    pousser(options, champ.ia(ia), "Ordonnance (IA)");
    if (champ.id === "lastNameBirth") pousser(options, mutuelle.nom, "Mutuelle");
    if (champ.id === "firstNamesUsed") pousser(options, mutuelle.prenom, "Mutuelle");
    if (champ.id === "dob") pousser(options, mutuelle.dateNaissance, "Mutuelle");
    const retenu = retenus[champ.id] || appointment[champ.appointment] || "";
    return {
      id: champ.id,
      label: champ.label,
      champRdv: champ.champRdv,
      options,
      retenu,
      divergents: options.length > 1,
    };
  }).filter((c) => c.options.length > 0);
}

export function champsDivergents(candidats) {
  return (candidats || []).filter((c) => c.divergents);
}

/**
 * De quelle source vient reellement une valeur, quand plusieurs la portent.
 *
 * Nicolas, 18/09 : « lorsqu'on a choisi la valeur, attention de bien remonter
 * la chips qui decoule de la source car dans cette image tu verras que le
 * nom, prenom, ddn, NIR... semblent saisis en manuel alors que non, ils
 * proviennent du Data Matrix. »
 *
 * Une valeur portee a la fois par le dossier ET par le Data Matrix n'est pas
 * une saisie manuelle : c'est une lecture machine que la saisie confirme.
 * C'est la machine qui doit apparaitre, parce que c'est elle qui apprend
 * quelque chose a la secretaire — « ce nom vient du code, je n'ai pas a le
 * reverifier ». « Manuel » dit l'inverse, et lui fait perdre du temps.
 *
 * L'ordre ci-dessous va donc de la source la plus sure a la moins sure, et
 * `Dossier / saisie` arrive en dernier : elle ne gagne que seule.
 */
const PRIORITE_SOURCES = [
  "Data Matrix INS",
  "Ordonnance (IA)",
  "Mutuelle",
  "Dossier / saisie",
];

/** Le nom de la source tel que la pastille du secretariat l'affiche. */
const ETIQUETTE_PASTILLE = {
  "Data Matrix INS": "Data Matrix",
  "Ordonnance (IA)": "IA/OCR",
  Mutuelle: "Mutuelle",
  "Dossier / saisie": "Manuel",
};

/**
 * La pastille d'un choix.
 *
 * `nomDuCode` est ce que la tablette a enregistre du code reellement lu
 * (`insReading.source` : « CNI », « e-ordo », « Identite INS »…). Nicolas,
 * 16/09 : « j'ai scanne le Data Matrix de ma CNI [...] on ne voit pas que ca
 * vient de la CNI ». Quand on l'a, il vaut mieux que le generique.
 */
export function pastillePourSources(sources, nomDuCode) {
  const trouvee = PRIORITE_SOURCES.find((s) => (sources || []).includes(s));
  if (!trouvee) return "";
  if (trouvee === "Data Matrix INS") return nomDuCode || "Data Matrix";
  return ETIQUETTE_PASTILLE[trouvee] || trouvee;
}

/**
 * Ce qu'on ecrit sur le rendez-vous quand la secretaire a tranche.
 *
 * On ecrit trois choses, et la troisieme manquait :
 *   — `identityRetenue`, la memoire du choix ;
 *   — le champ du rendez-vous lui-meme (`lastName`, `birthday`…) ;
 *   — `identitySources`, la PROVENANCE du choix. Sans elle, la pastille
 *     restait celle d'avant — c'est-a-dire « Manuel » — alors que la valeur
 *     retenue venait du Data Matrix.
 *
 * `identitySources` n'est pas ecrase en entier : on FUSIONNE avec celui qui
 * est deja sur le rendez-vous, pour ne pas effacer la provenance des champs
 * que la secretaire n'a pas touches.
 */
export function patchIdentiteRetenue(candidats, appointment = {}) {
  const nomDuCode = appointment.insReading?.source || "";
  const sourcesExistantes = appointment.identitySources || {};
  const patch = { identityRetenue: {}, identitySources: { ...sourcesExistantes } };

  for (const c of candidats || []) {
    if (!c.retenu) continue;
    patch.identityRetenue[c.id] = c.retenu;
    if (c.champRdv) patch[c.champRdv] = c.retenu;

    const choisie = (c.options || []).find(
      (o) => normaliser(o.valeur) === normaliser(c.retenu)
    );
    const pastille = pastillePourSources(choisie?.sources, nomDuCode);
    if (pastille) patch.identitySources[c.id] = pastille;
  }
  return patch;
}

/**
 * Les memes candidats, mais ranges en TABLEAU : une colonne par source, une
 * ligne par champ.
 *
 * Nicolas, 18/09 : « Le systeme de discordance d'identite est mal fait.
 * J'imagine un tableau avec en en-tete le nom des sources et dans les
 * differentes lignes ce qui a ete lu avec les eventuelles discordances et en
 * effet quelle valeur conservee. »
 *
 * La liste d'options empilees obligeait a lire chaque etiquette pour savoir
 * qui disait quoi. Un tableau le montre d'un coup d'oeil : la colonne
 * « Mutuelle » qui ne dit pas la meme chose que les autres saute aux yeux,
 * et c'est exactement l'information utile.
 *
 * Les colonnes sont dans l'ordre de `PRIORITE_SOURCES` — la plus sure a
 * gauche — et seules celles qui portent quelque chose sont rendues : un
 * tableau a quatre colonnes vides ne serait pas plus lisible que la liste.
 */
export function tableauDesSources(candidats) {
  const presentes = new Set();
  for (const c of candidats || []) {
    for (const o of c.options || []) {
      for (const s of o.sources || []) presentes.add(s);
    }
  }
  const sources = PRIORITE_SOURCES.filter((s) => presentes.has(s));

  /*
    UNE VALEUR N'APPARAIT QUE DANS UNE COLONNE, ET C'EST LE CORRECTIF DU 18/09
    (18h18).

    Nicolas : « les infos qui ont ete conservees proviennent du Data Matrix
    hors il y a aussi une colonne Dossier / Saisie qui nous embete et qui
    semble mettre la source en "Manuel". »

    Sa capture montre le probleme : « GARCIA-HAMMADI » apparaissait DEUX fois
    sur la meme ligne, en vert des deux cotes -- une fois sous « Data Matrix
    INS », une fois sous « Dossier / saisie ». C'est la meme valeur, lue par la
    machine puis confirmee par le dossier ; la repeter ne dit rien de plus, et
    la colonne de droite fait croire qu'il existe une saisie manuelle
    concurrente. Le tableau devenait alors le contraire de ce qu'il doit
    etre : au lieu de montrer un desaccord, il en inventait un.

    Chaque valeur ne se pose donc plus que dans la colonne de sa source la
    plus sure (`PRIORITE_SOURCES`). Les autres sources qui la confirment sont
    nommees a cote d'elle, en petit : l'information n'est pas perdue, elle
    cesse juste d'occuper une colonne. Consequence directe : quand la saisie
    ne fait que confirmer, la colonne « Dossier / saisie » se vide
    entierement, et elle disparait d'elle-meme.
  */
  const lignes = (candidats || []).map((c) => {
    const valeurs = {};
    const confirmations = {};
    for (const o of c.options || []) {
      const principale = PRIORITE_SOURCES.find((s) => (o.sources || []).includes(s));
      if (!principale) continue;
      if (valeurs[principale]) continue;
      valeurs[principale] = o.valeur;
      confirmations[principale] = (o.sources || []).filter((s) => s !== principale);
    }
    return {
      id: c.id,
      label: c.label,
      champRdv: c.champRdv,
      options: c.options,
      retenu: c.retenu,
      divergent: c.divergents,
      valeurs,
      confirmations,
    };
  });

  // On ne garde que les colonnes qui portent VRAIMENT quelque chose apres ce
  // rangement : une source qui ne fait que confirmer n'a plus de colonne.
  const utilisees = sources.filter((s) => lignes.some((l) => l.valeurs[s]));

  return { sources: utilisees, lignes };
}
